import type { AdminOverview, ApiError, ErrorCode, HealthReport, TokenResponse } from "./types";

// fetch wrapper for the Maintenance Monkey API. credentials:"include" sends the mm_token httpOnly
// cookie on every call; the dev server proxies /api and /actuator to the Spring app so the request
// stays same origin. Every non 2xx body is parsed as the ApiError envelope and raised as ApiClientError.

// Raised for any non 2xx response. Carries the parsed envelope when the body was the ApiError shape.
export class ApiClientError extends Error {
	readonly status: number;
	readonly code: ErrorCode;
	readonly envelope: ApiError | null;

	constructor(status: number, envelope: ApiError | null, fallbackMessage: string) {
		super(envelope?.message ?? fallbackMessage);
		this.name = "ApiClientError";
		this.status = status;
		this.envelope = envelope;
		this.code = envelope?.code ?? "UNKNOWN";
	}
}

// True when the parsed body carries the envelope's required members.
function isApiError(body: unknown): body is ApiError {
	return (
		typeof body === "object" &&
		body !== null &&
		typeof (body as ApiError).code === "string" &&
		typeof (body as ApiError).message === "string"
	);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	let res: Response;
	try {
		res = await fetch(path, {
			credentials: "include",
			headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}) },
			...init,
		});
	} catch {
		// Network reachability failure, before any HTTP status. No envelope exists to parse.
		throw new ApiClientError(0, null, "Cannot reach the service.");
	}

	const raw = await res.text();
	const body: unknown = raw ? safeJson(raw) : null;

	if (!res.ok) {
		throw new ApiClientError(res.status, isApiError(body) ? body : null, res.statusText || "Request failed");
	}
	return body as T;
}

// Parse JSON without throwing on a non JSON body (an upstream error page, say).
function safeJson(raw: string): unknown {
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

export const api = {
	// Validates credentials, sets the mm_token cookie, and returns the token envelope.
	login(email: string, password: string): Promise<TokenResponse> {
		return request<TokenResponse>("/api/v1/auth/login", {
			method: "POST",
			body: JSON.stringify({ email, password }),
		});
	},

	// Clears the mm_token cookie server side.
	logout(): Promise<void> {
		return request<void>("/api/v1/auth/logout", { method: "POST" });
	},

	// Admin only aggregate for the dashboard. 403 for a non admin principal, 401 when unauthenticated.
	overview(): Promise<AdminOverview> {
		return request<AdminOverview>("/api/v1/admin/overview");
	},

	// Public liveness and readiness roll up. Details appear only when the caller is authorized.
	health(): Promise<HealthReport> {
		return request<HealthReport>("/actuator/health");
	},
};
